import Card from "@/components/Card"
import InputError from "@/components/InputError"
import SkeletonRow from "@/components/SkeletonRow"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import api from "@/lib/axios"
import { DataBerkas, DataSiswa } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaCheck, FaFaceSmileBeam } from "react-icons/fa6"
import { GiBackwardTime } from "react-icons/gi"
import { MdBlockFlipped } from "react-icons/md"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"

type DataIjazah = {
  id_data_pendaftaran: string
  nomor_pendaftaran: string
  siswa: DataSiswa
  ijazah: DataBerkas
  status_loket_4_1: number
  keterangan_loket_4_1: string
}

type ValidasiGlobalState = {
  id_data_pendaftaran: string
  status_loket_4_1: number
  keterangan_loket_4_1: string
}

const patchDataValidasiGlobal = async (form: ValidasiGlobalState) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/loket-4/data-ijazah/validasi-global",
    form
  )
  return response.data
}

const patchDataResetValidasi = async (id_data_pendaftaran: string) => {
  const response = await api.patch(
    "/verifikator/daftar-kembali/reset?loket=4&menu=1",
    { id_data_pendaftaran }
  )
  return response.data
}

export default function Ijazah({
  data,
  isPending,
  error,
}: {
  data: DataIjazah
  isPending: boolean
  error: AxiosError | null
}) {
  const [searchParams] = useSearchParams()
  const idParams = searchParams.get("id")

  const [form, setForm] = useState({
    catatan: "",
  })

  const queryClient = useQueryClient()
  const {
    mutate: mutateValidationGlobal,
    isPending: isPendingValidationGlobal,
    error: errorValidationGlobal,
  } = useMutation({
    mutationFn: patchDataValidasiGlobal,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataIjazah", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const {
    mutate: mutateResetValidation,
    isPending: isPendingResetValidation,
    // error: errorResetValidation,
  } = useMutation({
    mutationFn: patchDataResetValidasi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["dataIjazah", idParams],
      })
      queryClient.invalidateQueries({
        queryKey: ["dataStatusLoket", idParams],
      })
      toast.success(data?.message || "Success.")
    },
    onError: (
      error: AxiosError<{ message?: string; errors?: Record<string, string[]> }>
    ) => {
      const err = error as AxiosError<{
        message?: string
        errors?: Record<string, string[]>
      }>
      const errorMessage =
        err.response?.data?.message || "An unexpected error occurred."
      toast.error(errorMessage)
    },
  })

  const handleValidasiGlobal = (status: number) => {
    mutateValidationGlobal({
      id_data_pendaftaran: data?.id_data_pendaftaran,
      status_loket_4_1: status,
      keterangan_loket_4_1: form.catatan,
    })
  }

  const handleReset = () => {
    mutateResetValidation(data?.id_data_pendaftaran || "")
  }

  if (isPending) {
    return (
      <div className="flex flex-col gap-y-3">
        {Array(10)
          .fill(null)
          .map((_, index) => {
            return <SkeletonRow key={index} />
          })}
      </div>
    )
  }

  if (error?.response?.status === 404) {
    return (
      <div>
        Data <span className="text-primary">{idParams}</span> tidak ditemukan.
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <div>
          <FaFaceSmileBeam className="mx-auto" size={40} />
          <h1 className="mt-3 text-xl font-semibold">
            Lakukan pencarian menggunakan nomor pendaftaran atau nama siswa.
          </h1>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-5">
      <Card>
        <div className="flex-1 h-[70vh] relative">
          {data?.ijazah?.path_berkas ? (
            <iframe
              src={data?.ijazah?.path_berkas || ""}
              title="Slip Gaji"
              className="absolute h-full w-full"
            />
          ) : (
            <div className="h-full flex justify-center items-center">
              <h1 className="text-2xl font-semibold">
                File ijazah tidak ditemukan.
              </h1>
            </div>
          )}
        </div>

        <Card className="mt-10 flex-1 overflow-auto">
          <table className="table-auto w-full border-collapse border rounded-xl overflow-hidden">
            <tbody>
              <tr>
                <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                  Status Validasi
                </th>
                <td className="py-2 px-4 border w-[5%]">:</td>
                <td className="py-2 px-4 border w-[50%]">
                  {data?.status_loket_4_1 === 1 ? (
                    <Badge variant={"dark"}>Sudah Valid</Badge>
                  ) : data?.status_loket_4_1 === 0 ? (
                    <Badge variant={"outline"}>Tidak Valid</Badge>
                  ) : (
                    <Badge variant={"secondary"}>Belum Valid</Badge>
                  )}
                </td>
              </tr>
              <tr>
                <th className="py-2 px-4 border w-[45%] font-semibold text-start">
                  Catatan
                </th>
                <td className="py-2 px-4 border w-[5%]">:</td>
                <td className="py-2 px-4 border w-[50%]">
                  {data?.keterangan_loket_4_1}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-5 px-4">
            {data?.status_loket_4_1 === null ? (
              <>
                {/* <h1 className="font-semibold text-xl">
                  Kesimpulan Hasil Validasi
                </h1>
                <p className="text-muted-foreground">
                  Jika terlalu banyak nilai yang tidak sesuai maka kesimpulan
                  status validasi dapat dinyatakan tidak valid.
                </p> */}
                <div className="mt-3">
                  <Label>Input Catatan</Label>
                  <Textarea
                    placeholder="Ketik catatan..."
                    className="mt-1"
                    value={form.catatan}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        catatan: event.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={errorValidationGlobal?.response?.data.errors?.keterangan_loket_4_1?.join(
                      ","
                    )}
                  />
                </div>
                <div className="mt-5 flex justify-end gap-3">
                  {isPendingValidationGlobal ? (
                    <Button
                      variant={"outline"}
                      className="flex items-center gap-3"
                      disabled
                    >
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Processing...
                      </>
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant={"outline"}
                        className="flex items-center gap-3"
                        onClick={() => handleValidasiGlobal(0)}
                        disabled={isPendingValidationGlobal}
                      >
                        <MdBlockFlipped />
                        Tidak Valid
                      </Button>
                      <Button
                        className="flex items-center gap-3"
                        onClick={() => handleValidasiGlobal(1)}
                        disabled={isPendingValidationGlobal}
                      >
                        <FaCheck />
                        Valid
                      </Button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  className="flex items-center gap-3"
                  onClick={() => handleReset()}
                  disabled={isPendingResetValidation}
                >
                  {isPendingResetValidation ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <GiBackwardTime />
                      Reset Hasil Validasi
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </Card>
      </Card>
    </div>
  )
}
